/* import __COLOCATED_TEMPLATE__ from './expanded-order.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type { ShopifyOrder, ShippingAddress } from '../shopify';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import copyToClipboard from 'copy-to-clipboard';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import ajax from 'embercom/lib/ajax';
import { captureException } from 'embercom/lib/sentry';
import ENV from 'embercom/config/environment';
//@ts-ignore
import ShopifyCreatedOrderDuplicate from 'embercom/components/notifications/shopify-created-order-duplicate';

interface Signature {
  Args: {
    order: ShopifyOrder;
    onCollapse: () => void;
    onShippingAddressUpdate?: (orderId: number, newShippingAddress: ShippingAddress) => void;
    onRefundSuccess?: () => void;
    onReplacementSuccess?: () => void;
    shopifyCustomerId?: number;
    conversationId: number;
    userId: string;
    key?: string;
  };
}

export default class ExpandedOrder extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare session: $TSFixMe;

  @tracked showEditShippingAddressModal = false;
  @tracked showRefundOrderModal = false;
  @tracked showAddReplacementItemModal = false;

  get tagsArray() {
    if (!this.args.order.tags) {
      return [];
    }
    return this.args.order.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
  }

  @action
  viewOrderInShopify() {
    if (this.args.order.order_url) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'view_order_in_shopify',
        object: 'shopify_order_action',
        section: 'inbox_sidebar',
        order_id: this.args.order.id,
        order_name: this.args.order.name,
        shopify_domain: this.args.order.store,
      });
      safeWindowOpen(this.args.order.order_url);
    }
  }

  @action
  copyOrder() {
    if (this.args.order.order_status_url) {
      copyToClipboard(this.args.order.order_status_url);
      this.notificationsService.notifyConfirmation(
        this.intl.t('inbox.conversation-details-sidebar.copy-to-clipboard.copied'),
      );
      this.intercomEventService.trackAnalyticsEvent({
        action: 'copy_order_url',
        object: 'shopify_order_action',
        section: 'inbox_sidebar',
        order_id: this.args.order.id,
        order_name: this.args.order.name,
        shopify_domain: this.args.order.store,
      });
    }
  }

  @action
  duplicateOrder() {
    taskFor(this.duplicateOrderTask).perform();
  }

  @action
  openRefundOrderModal() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'refund_order_button',
      section: 'inbox_sidebar',
      shopify_domain: this.args.order.store,
    });
    this.showRefundOrderModal = true;
  }

  @action
  closeRefundOrderModal() {
    this.showRefundOrderModal = false;
  }

  @action
  openEditShippingAddressModal() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'edit_shipping_address_button',
      section: 'inbox_sidebar',
      shopify_domain: this.args.order.store,
    });
    this.showEditShippingAddressModal = true;
  }

  @action
  closeEditShippingAddressModal() {
    this.showEditShippingAddressModal = false;
  }

  @action
  openAddReplacementItemModal() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'add_replacement_item_button',
      section: 'inbox_sidebar',
      shopify_domain: this.args.order.store,
    });
    this.showAddReplacementItemModal = true;
  }

  @action
  closeAddReplacementItemModal() {
    this.showAddReplacementItemModal = false;
  }

  @task({ restartable: true })
  *duplicateOrderTask(): TaskGenerator<void> {
    try {
      let shopifyDomain = this.args.order.store;
      if (!shopifyDomain) {
        this.notificationsService.notifyError(
          this.intl.t('inbox.conversation-details-sidebar.shopify.order.duplicate-error-no-store'),
        );
        return;
      }

      if (!this.args.shopifyCustomerId) {
        this.notificationsService.notifyError(
          this.intl.t(
            'inbox.conversation-details-sidebar.shopify.order.duplicate-error-no-customer',
          ),
        );
        return;
      }

      let lineItems = [];

      for (let item of this.args.order.line_items) {
        try {
          // Search for the product by title or SKU
          let searchQuery = item.sku || item.title;
          let productResponse = yield ajax({
            url: '/ember/inbox/shopify/search_products',
            type: 'GET',
            data: {
              app_id: this.session.workspace.id,
              query: searchQuery,
              shopify_domain: shopifyDomain,
            },
          });

          let matchingProduct = productResponse.products.find(
            (product: any) =>
              product.sku === item.sku ||
              product.display_name.toLowerCase().includes(item.title.toLowerCase()),
          );

          if (matchingProduct) {
            let quantity = 1;
            if (item.subtitle) {
              let quantityMatch = item.subtitle.match(/×\s*(\d+)/);
              if (quantityMatch) {
                quantity = parseInt(quantityMatch[1], 10);
              }
            }

            lineItems.push({
              variant_id: matchingProduct.variant_id,
              quantity,
            });
          } else {
            console.warn(`Could not find product for item: ${item.title}`);
          }
        } catch (error) {
          console.warn(`Error searching for product ${item.title}:`, error);
        }
      }

      if (lineItems.length === 0) {
        this.notificationsService.notifyError(
          this.intl.t(
            'inbox.conversation-details-sidebar.shopify.order.duplicate-error-no-products',
          ),
        );
        return;
      }

      let params = {
        app_id: this.session.workspace.id,
        customer_id: this.args.shopifyCustomerId,
        shopify_domain: shopifyDomain,
        line_items: lineItems,
        paid: false,
        shipping: 'free',
        note: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.order.duplicated-from-order',
          {
            orderName: this.args.order.name,
          },
        ),
        tags: this.args.order.tags || '',
        duplicate: true,
      };

      if (this.args.order.shipping_address) {
        Object.assign(params, {
          shipping_address: this.args.order.shipping_address,
        });
      }

      let response = yield ajax({
        url: '/ember/inbox/shopify/create_draft_order',
        type: 'POST',
        data: JSON.stringify(params),
      });

      let notificationDuration = ENV.APP._1000MS * 30;
      yield this.notificationsService.notifyConfirmationWithModelAndComponent(
        {
          id: response.id,
          url: response.url,
        },
        ShopifyCreatedOrderDuplicate,
        notificationDuration,
      );

      this.intercomEventService.trackAnalyticsEvent({
        action: 'duplicate_order',
        object: 'shopify_order_action',
        section: 'inbox_sidebar',
        shopify_domain: this.args.order.store,
      });
    } catch (error) {
      captureException(error);
      this.notificationsService.notifyError(
        this.intl.t('inbox.conversation-details-sidebar.shopify.order.duplicate-error'),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::Shopify::ExpandedOrder': typeof ExpandedOrder;
    'inbox2/conversation-details-sidebar/sidebar-sections/shopify/expanded-order': typeof ExpandedOrder;
  }
}
